import {
  AddIcon,
  CollapseSidebarIcon,
  ExpandSidebarIcon,
  MenuIcon,
} from '@/components/icons';
import { Button, Drawer, Grid, Layout, theme, Tooltip } from 'antd';
import { Suspense, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { BrandLockup, Logomark } from '@/components/Brand';
import { TopProgress } from '@/components/Loader';
import { RoutePageLoader } from './RoutePageLoader';
import { AccountMenu } from './AccountMenu';
import { useAuth } from '@/auth/auth-context';
import { usePharmacyFeatures } from '@/auth/features';
import { useThemeMode } from '@/theme/theme-provider';
import { LAYOUT, NAV } from '@/theme/tokens';
import { AvailabilityPill } from './AvailabilityPill';
import { ConnectionPill } from './ConnectionPill';
import { NewOrdersPill } from './NewOrdersPill';
import { NewPrescriptionsPill } from './NewPrescriptionsPill';
import { useNewPrescriptionCount } from '@/lib/use-new-prescriptions';
import { BranchSwitcher } from './BranchSwitcher';
import {
  ALL_NAV_ITEMS,
  groupKeyForPath,
  NAVIGATION,
  NAVIGATION_FOOTER,
  SETTINGS_SECTION,
  isGroup,
  type NavItem,
} from './navigation';
import { SidebarNav } from './SidebarNav';
import { TopbarSlotsContext, type TopbarSlots } from './topbar-slots';
import { ariaShortcut, bindingCaps, useBindings, useKeyAction } from '@/lib/keybindings';
import { closeNewProduct, openNewProduct, useNewProductOpen } from '@/lib/new-product';
import { useActivePharmacy } from '@/auth/active-pharmacy';
import { NewProductDrawer } from '@/features/purchase/NewProductDrawer';
import { usePersistentState, type Codec } from '@/lib/persistent-state';

/* Folded or not, remembered per device: a choice about the screen in front of
   somebody, which is why it is not on their account. */
const COLLAPSED_CODEC: Codec<boolean, boolean> = {
  version: 1,
  encode: (v) => v,
  decode: (raw) => (typeof raw === 'boolean' ? raw : null),
  isEmpty: (v) => !v,
};
import { usePlatformTimezoneSync } from '@/lib/use-platform-timezone';
import { useNewOrderCount, usePushRefresh } from '@/lib/use-new-orders';
import { syncPushSubscription } from '@/lib/push';

const { Header, Sider, Content } = Layout;

export function AppLayout() {
  // The clock every date under this shell is rendered on. See the hook.
  usePlatformTimezoneSync();

  /*
   * A push makes the chrome refetch at once rather than at the next poll, so
   * the badge and the lock screen cannot disagree. Mounted here because this
   * is the one component that is always on screen.
   */
  usePushRefresh();

  /*
   * Re-register this device's push subscription on every launch.
   *
   * Cheap, and it heals what nobody would otherwise notice: the push service
   * rotated the endpoint, our row was pruned after a failed send, or a
   * different member of staff signed in on this tablet. It only acts when
   * permission has already been granted - the prompt itself must come from a
   * tap, never from a page load.
   *
   * Does nothing in development: the service worker is registered in
   * production only, so `serviceWorker.ready` never resolves here. See
   * main.tsx.
   */
  useEffect(() => {
    void syncPushSubscription();
  }, []);

  /*
   * Read here FOR THE SIDEBAR, which cannot read it itself: `SidebarNav` is
   * meant to be one component shared with the admin panel, and a data
   * dependency on an order queue that only exists in this app is how that
   * stops being true. So it takes a number.
   *
   * `NewOrdersPill` calls the same hook rather than taking a prop, matching
   * the pills either side of it. That is not a second request - both land on
   * one react-query cache entry, so the two cannot show different numbers.
   */
  const newOrders = useNewOrderCount();
  /* The same for prescriptions waiting to be read: see NewPrescriptionsPill. */
  const newPrescriptions = useNewPrescriptionCount();

  const { token } = theme.useToken();
  const { mode } = useThemeMode();
  const { can } = useAuth();
  const features = usePharmacyFeatures();
  const navigate = useNavigate();
  const location = useLocation();
  const screens = Grid.useBreakpoint();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = usePersistentState<boolean, boolean>(
    'tlp.ui.sidebar.collapsed',
    () => false,
    COLLAPSED_CODEC,
  );
  const keys = useBindings();
  const toggleHint = bindingCaps(keys.toggleSidebar).join(' ');

  /*
   * The topbar's page slots (see topbar-slots.tsx). State rather than refs, so
   * a page that mounts before the bar has painted re-renders into it once the
   * elements exist.
   */
  const [titleSlot, setTitleSlot] = useState<HTMLElement | null>(null);
  const [actionsSlot, setActionsSlot] = useState<HTMLElement | null>(null);
  const topbarSlots = useMemo<TopbarSlots>(
    () => ({ title: titleSlot, actions: actionsSlot }),
    [titleSlot, actionsSlot],
  );

  /*
   * Does the page's own action set include a filled button? Read off the slot
   * rather than declared by each page, so no page can forget to say so. The
   * observer only fires when the slot's subtree changes - a page mounting,
   * a button appearing - not on every render.
   */
  const [pageHasPrimary, setPageHasPrimary] = useState(false);
  useEffect(() => {
    if (!actionsSlot) return;
    const check = () =>
      setPageHasPrimary(Boolean(actionsSlot.querySelector('.ant-btn-variant-solid')));
    check();
    const observer = new MutationObserver(check);
    observer.observe(actionsSlot, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [actionsSlot]);
  const onSale = location.pathname === '/sale' || location.pathname.startsWith('/sale/');

  /*
   * Ctrl+B, as in every editor and in the reference admin. On a narrow screen
   * there is no rail to fold to, so the same key opens and closes the drawer.
   */
  useKeyAction('toggleSidebar', (event) => {
    event.preventDefault();
    if (isCompact) setDrawerOpen((open) => !open);
    else setCollapsed((current) => !current);
  });

  /*
   * The two things somebody reaches for from anywhere: a new bill, and a
   * product the catalogue does not have. Registered here, once, for every
   * screen, and only for someone allowed to do them, so the key stays free
   * for anybody who is not.
   */
  const canBill = can('order:fulfil');
  const { pharmacyId } = useActivePharmacy();
  const canAddProduct = can('purchase:write') && Boolean(pharmacyId);
  const newProductOpen = useNewProductOpen();

  useKeyAction(
    'newBill',
    (event) => {
      event.preventDefault();
      if (!onSale) navigate('/sale');
    },
    { enabled: canBill },
  );

  useKeyAction(
    'newProduct',
    (event) => {
      event.preventDefault();
      openNewProduct();
    },
    { enabled: canAddProduct },
  );

  /*
   * A counter runs on a tablet as often as on a desktop, and a sidebar that
   * merely collapses to icons still eats a third of a 10-inch screen while
   * someone is serving. Below `lg` the navigation becomes a drawer instead -
   * the one place this layout and the admin panel's deliberately differ.
   */
  const isCompact = !screens.lg;

  // Navigating must close the drawer, or the next screen opens behind it.
  useEffect(() => setDrawerOpen(false), [location.pathname]);

  /*
   * Navigation is filtered by permission AND by what the shop was signed up
   * for - same rule the backend follows. Hiding a link is a courtesy, never a
   * control: the route guard and then the server both check again.
   */
  const visible = useMemo(
    () => (item: NavItem) => {
      if (item.permissions && !item.permissions.some(can)) return false;
      if (item.feature === 'marketplace' && !features.marketplace) return false;
      if (item.feature === 'prescriptions' && !features.prescriptions) return false;
      return true;
    },
    [can, features.marketplace, features.prescriptions],
  );

  /*
   * Sections open themselves when you are inside one, and otherwise stay shut.
   *
   * Held as state rather than derived, so opening the stock book to compare two
   * screens does not snap closed on the next render - but seeded from the
   * route, because landing on a deep link with the section folded leaves you
   * looking at a sidebar that does not contain the page you are on.
   *
   * The seed is resolved through the longest matching nav path rather than the
   * raw location: on /purchases/abc nothing would match exactly, and arriving
   * at a purchase from a link would fold the stock book away under you.
   */
  const groupKey = useMemo(() => {
    const matches = ALL_NAV_ITEMS.map((item) => item.path)
      .filter((path) => location.pathname === path || location.pathname.startsWith(`${path}/`))
      .sort((a, b) => b.length - a.length);
    return groupKeyForPath(matches[0] ?? '/');
  }, [location.pathname]);

  /*
   * Where you are, for the topbar: the page, and the section it sits in. The
   * longest matching path wins, so /purchase/history is History, not Purchase.
   */
  const crumb = useMemo(() => {
    const best = ALL_NAV_ITEMS.filter(
      (item) => location.pathname === item.path || location.pathname.startsWith(`${item.path}/`),
    ).sort((a, b) => b.path.length - a.path.length)[0];
    if (!best) return null;
    const group = [...NAVIGATION, ...NAVIGATION_FOOTER, SETTINGS_SECTION].find(
      (entry) => isGroup(entry) && entry.items.some((item) => item.key === best.key),
    );
    return { item: best, group: group && isGroup(group) ? group : null };
  }, [location.pathname]);

  const [openKeys, setOpenKeys] = useState<string[]>(() => (groupKey ? [groupKey] : []));

  useEffect(() => {
    if (groupKey)
      setOpenKeys((current) => (current.includes(groupKey) ? current : [...current, groupKey]));
  }, [groupKey]);

  /*
   * One sidebar, rendered either into the Sider or into the drawer. Building it
   * twice is how the two drift, and the tablet is not a lesser version of this
   * screen - it is the one a counter actually runs on.
   */
  const shell = (folded: boolean) => (
    <>
      <div
        className="flex shrink-0 items-center justify-between gap-2"
        style={{
          height: LAYOUT.headerHeight,
          paddingInline: folded ? 0 : NAV.gutter,
          justifyContent: folded ? 'center' : undefined,
        }}
      >
        {/*
          No rule under the band. It used to draw one to line up with the
          header's, and the header now sits inside the floating card, inset from
          the frame - a rule here would line up with nothing.

          The fold toggle lives here rather than in the header, beside the thing
          it folds. Folded, it is the whole band: the cross alone was a logo
          nobody needed at forty pixels, and the toggle is what that rail is for.
        */}
        {folded ? null : <BrandLockup height={34} />}
        {isCompact ? null : (
          <Tooltip
            placement="right"
            title={
              <span className="inline-flex items-center gap-2">
                {folded ? 'Expand navigation' : 'Collapse navigation'}
                <span className="entry-keys-inline">
                  {bindingCaps(keys.toggleSidebar).map((cap) => (
                    <kbd key={cap}>{cap}</kbd>
                  ))}
                </span>
              </span>
            }
          >
            <Button
              type="text"
              size="small"
              className="nav-fold"
              icon={folded ? <ExpandSidebarIcon /> : <CollapseSidebarIcon />}
              onClick={() => setCollapsed((current) => !current)}
              aria-label={`${folded ? 'Expand' : 'Collapse'} navigation (${toggleHint})`}
            />
          </Tooltip>
        )}
      </div>

      {/*
        The branch being worked in, at the top of the thing it scopes. It lived
        in the page header, across the screen from the Stock and Bills links it
        governs - so landing on an empty screen and not knowing which shop you
        were looking at was easy.
      */}
      {folded ? null : (
        <div className="shrink-0 px-3 pt-3 pb-1">
          <BranchSwitcher />
        </div>
      )}

      <div className="app-scroll min-h-0 flex-1 overflow-y-auto">
        <SidebarNav
          entries={NAVIGATION}
          openKeys={openKeys}
          onOpenChange={setOpenKeys}
          collapsed={folded}
          visible={visible}
          label="The counter"
          badges={{ 'new-orders': newOrders, 'new-prescriptions': newPrescriptions }}
        />
      </div>

      <div className="shrink-0" style={{ borderTop: '1px solid var(--tlp-nav-divider)' }}>
        {/*
         * The second landmark, and the reason `SidebarNav` takes a label: two
         * unnamed `<nav>`s give a screen reader two identical "navigation"
         * entries with no way to tell the work from the settings.
         *
         * It shares `openKeys` with the nav above rather than keeping its own.
         * A section is a section wherever it is drawn, and a second piece of
         * state would mean the route-seeding effect had to know which of the
         * two lists a path had come from.
         */}
        <SidebarNav
          entries={NAVIGATION_FOOTER}
          openKeys={openKeys}
          onOpenChange={setOpenKeys}
          collapsed={folded}
          visible={visible}
          label="Settings"
        />

        {/* Who you are signed in as, where a desktop app puts it. */}
        <AccountMenu collapsed={folded} />
      </div>
    </>
  );

  return (
    <Layout className="h-full" style={{ background: 'var(--tlp-nav-bg)' }}>
      {isCompact ? (
        <Drawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          placement="left"
          width={LAYOUT.siderWidth}
          closable={false}
          styles={{ body: { padding: 0, background: 'var(--tlp-nav-bg)' } }}
          classNames={{ body: 'flex min-h-0 flex-col' }}
        >
          {shell(false)}
        </Drawer>
      ) : (
        <Sider
          theme={mode === 'dark' ? 'dark' : 'light'}
          width={LAYOUT.siderWidth}
          collapsedWidth={LAYOUT.siderCollapsedWidth}
          collapsible
          collapsed={collapsed}
          trigger={null}
          style={{
            /*
             * The nav ground from the palette, by its own variable.
             *
             * Not `colorBgContainer`: an inline style beats the theme's
             * `siderBg` every time, and that one briefly painted the sider
             * white while its inks were tuned for dark. Reading the nav's own
             * variable means the sider and the inks drawn on it always come
             * from the same place.
             */
            /* No edge: the sider is the frame now, and the content floats on it. */
            background: 'var(--tlp-nav-bg)',
          }}
          /*
           * The brand band and the account row stay put; only the navigation
           * between them scrolls. With the whole sider scrolling, a long menu
           * carried the logo off the top of the screen - and the band's hairline
           * is what lines up with the content header's, so it has to hold.
           *
           * Both halves of that are needed. antd puts `.ant-layout-sider-children`
           * between the Sider root and this markup, so a flex column on the root
           * alone leaves the wrapper a block: `flex-1` never resolves, the nav
           * grows past the viewport, and the root's `overflow: hidden` clips it
           * with no way to reach the last item.
           */
          classNames={{ body: 'flex min-h-0 flex-col' }}
          className="flex flex-col overflow-hidden!"
        >
          {shell(collapsed)}
        </Sider>
      )}

      {/*
        The floating card: the page and its header, inset from the frame on
        three sides and rounded, as the reference admin draws it. The sider and
        this card are two objects on one ground, which is what lets the eye tell
        chrome from work without a rule between them. Narrow screens drop the
        inset - on a phone it would only spend width.
      */}
      <Layout className="app-card">
        {/*
          The topbar is the first thing in the page's own scroller, sticky at
          its top. Sharing the scroller is what makes the edges agree: the
          scrollbar runs beside both, as it does down the reference admin's
          card, instead of beside the page alone.
        */}
        {/* A thin bar along the top while something saves or first loads. */}
        <TopProgress />
        <TopbarSlotsContext value={topbarSlots}>
        <Content className="app-scroll app-content-scroll overflow-y-auto">
          <Header
            className="sticky top-0 z-10 shrink-0"
            style={{
              /* The card's own ground, so the header reads as its top edge
               rather than a bar laid across it. */
              background: token.colorBgLayout,
              padding: 0,
            }}
          >
            {/*
            The same wrapper as the page below it - centred, the same max width,
            the same side padding - so the topbar's right edge and the page's
            are one line at every window width. With the bar outside the
            scroller it carried 14px of padding against the page's 20px plus a
            scrollbar, and New bill stuck out past Save bill.
          */}
            <div
              className="mx-auto flex h-full items-center justify-between gap-3 px-3 sm:px-5"
              style={{ maxWidth: LAYOUT.contentMaxWidth }}
            >
              <div className="flex min-w-0 items-center gap-2">
                {isCompact ? (
                  <>
                    <Button
                      type="text"
                      icon={<MenuIcon />}
                      onClick={() => setDrawerOpen(true)}
                      aria-label={`Open navigation (${toggleHint})`}
                    />
                    <Logomark size={26} />
                  </>
                ) : null}
                {/*
                  The nav's label is the page's <h1> until the page names
                  itself: a PageHeader portals its own title (and info button)
                  into the slot, and the default is hidden by CSS while the
                  slot has anything in it. One heading either way.
                */}
                <span className="topbar-crumb">
                  {crumb
                    ? (() => {
                        const Icon = (crumb.group ?? crumb.item).icon;
                        return Icon ? <Icon /> : null;
                      })()
                    : null}
                  {crumb?.group ? (
                    <>
                      <span className="topbar-group">{crumb.group.label}</span>
                      <span className="topbar-sep" aria-hidden>
                        /
                      </span>
                    </>
                  ) : null}
                  {crumb ? <h1 className="topbar-page topbar-page-default">{crumb.item.label}</h1> : null}
                  <span ref={setTitleSlot} className="topbar-title-slot" />
                </span>
              </div>

              {/*
            What is true of the shop, then the one action this app exists for.
            The account and the theme switch used to sit here too; they are
            preferences about a person rather than facts about the counter, and
            they now live at the foot of the sidebar.
          */}
              <div className="flex items-center gap-2">
                {/* State of the world. Connection only appears when it is wrong. */}
                <ConnectionPill />
                <NewOrdersPill />
                <NewPrescriptionsPill />
                <AvailabilityPill />

                {/*
                 * The divider belongs to the button, not to the row. Left standing
                 * on its own it produced two rules with nothing between them for
                 * anyone without permission to bill.
                 */}
                {/*
                  The one action this app exists for - except on the till
                  itself, where it would be a button to the page you are on.
                  Filled unless the page shows a filled button of its own, so
                  the bar never carries two.
                */}
                {canBill && !onSale ? (
                  <Tooltip title={`New bill · ${bindingCaps(keys.newBill).join(' + ')}`}>
                    <Button
                      type={pageHasPrimary ? 'default' : 'primary'}
                      icon={<AddIcon />}
                      onClick={() => navigate('/sale')}
                      className="hidden sm:inline-flex"
                      aria-keyshortcuts={ariaShortcut(keys.newBill)}
                    >
                      New bill
                    </Button>
                  </Tooltip>
                ) : null}

                {/* The page's own actions, last, so its primary ends the bar. */}
                <div ref={setActionsSlot} className="topbar-actions" />
              </div>
            </div>
          </Header>
          <div
            className="mx-auto px-3 py-4 sm:px-5 sm:py-5"
            style={{ maxWidth: LAYOUT.contentMaxWidth }}
          >
            {/* The one loading boundary for pages: see `guarded` in routes.tsx. */}
            <Suspense fallback={<RoutePageLoader />}>
              <Outlet />
            </Suspense>
          </div>
        </Content>
        </TopbarSlotsContext>
      </Layout>

      {/* One "Add new product" for the whole app: the shortcut and any page's
          button open this. A product added here is a draft the platform
          curates; it can be booked in on the Purchase screen straight away. */}
      {canAddProduct && pharmacyId ? (
        <NewProductDrawer
          open={newProductOpen}
          pharmacyId={pharmacyId}
          initialName=""
          purpose="catalogue"
          onClose={closeNewProduct}
          // Stays open, cleared, for the next product; Done closes it.
          onCreated={() => {}}
        />
      ) : null}
    </Layout>
  );
}
